import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/data";
import { formatClubDateTime } from '../../utils/clubTime';

// Use Cognito user pool auth for admin update operations
const client = generateClient({ authMode: 'userPool' });

interface PreRegistration {
  id: string;
  eventId: string;
  memberSub: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  adminNotes?: string;
  registrationDate: string;
  amountPaid?: number;
  paymentReceived?: boolean;
  paymentAdjustmentReason?: string;
  squarePaymentId?: string;
  createdAt: string;
  updatedAt: string;
}

interface Member {
  id: string;
  cognitoSub: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface PreRegistrationApprovalProps {
  eventId: string;
  eventTitle: string;
  eventDate: string;
  onClose: () => void;
}

const PreRegistrationApproval: React.FC<PreRegistrationApprovalProps> = ({
  eventId,
  eventTitle,
  eventDate,
  onClose,
}) => {
  const [preRegistrations, setPreRegistrations] = useState<PreRegistration[]>(
    [],
  );
  const [members, setMembers] = useState<Record<string, Member>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [approvingReg, setApprovingReg] = useState<PreRegistration | null>(null);
  const [approvalAmount, setApprovalAmount] = useState<number>(0);
  const [approvalAdjustmentReason, setApprovalAdjustmentReason] = useState("");
  const [approvalPaymentReceived, setApprovalPaymentReceived] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  // #157 (S1): rows skipped by loadPreRegistrations because they came back
  // null with a GraphQL error (missing required field) — shown as an amber
  // notice rather than aborting the load.
  const [unreadableCount, setUnreadableCount] = useState(0);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    loadPreRegistrations();
  }, [eventId]);

  const loadPreRegistrations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Query pre-registrations for this event. #79-class: page fully — a
      // filtered list() caps rows SCANNED, not returned, so a popular
      // event's pre-registrations could be silently undercounted.
      //
      // #157 (S1): a row missing a required field comes back as null with
      // a GraphQL error rather than throwing. Keep paging, skip the null
      // rows (counted below for the amber notice), and never abort the
      // whole load on their account — one bad row used to make the entire
      // screen show "Failed to load pre-registrations".
      let allPreRegs: any[] = [];
      const allErrors: any[] = [];
      let unreadable = 0;
      let preRegToken: string | null | undefined;
      do {
        const page: any = await client.models.EventPreRegistration.list({
          filter: { eventId: { eq: eventId } },
          nextToken: preRegToken || undefined,
        });
        const items: any[] = page.data || [];
        const readable = items.filter((pr: any) => pr && typeof pr === 'object');
        unreadable += items.length - readable.length;
        if (page.errors?.length) {
          allErrors.push(...page.errors);
          console.warn('PreRegistrationApproval: skipped unreadable pre-registration records', page.errors.map((e: any) => e?.path?.join('.')));
        }
        allPreRegs = allPreRegs.concat(readable);
        preRegToken = page.nextToken;
      } while (preRegToken);

      setUnreadableCount(unreadable);

      {
        const sorted = [...allPreRegs].sort(
          (a, b) =>
            new Date(a.registrationDate).getTime() -
            new Date(b.registrationDate).getTime(),
        );
        setPreRegistrations(sorted as PreRegistration[]);

        // Load member details for each pre-registration
        const memberSubs = Array.from(
          new Set(sorted.map((pr) => pr.memberSub)),
        );
        const memberMap: Record<string, Member> = {};

        for (const memberSub of memberSubs) {
          try {
            // #79-class: page fully — a single page could miss the match
            // for this memberSub even though cognitoSub is expected unique,
            // since the filter caps rows SCANNED, not returned.
            let found: Member | null = null;
            let memberToken: string | null = null;
            do {
              const memberResult: any = await client.models.Member.list({
                filter: { cognitoSub: { eq: memberSub } },
                ...(memberToken ? { nextToken: memberToken } : {}),
              });
              // #157 (S1): a null/unreadable row on this page must not be
              // mistaken for "no match on this page" — filter it out before
              // checking for a hit, so a genuine match elsewhere on the
              // same page is still found.
              const readable = (memberResult.data || []).filter((m: any) => m && typeof m === 'object');
              if (readable.length > 0) {
                found = readable[0] as Member;
                break;
              }
              memberToken = memberResult.nextToken ?? null;
            } while (memberToken);

            if (found) {
              memberMap[memberSub] = found;
            }
          } catch (err) {
            console.error(`Error loading member ${memberSub}:`, err);
            // Continue loading other members
          }
        }

        setMembers(memberMap);
      }
    } catch (err) {
      console.error("Error loading pre-registrations:", err);
      setError("Failed to load pre-registrations");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (preReg: PreRegistration) => {
    setApprovalAmount(preReg.amountPaid ?? 10); // Default competition fee — admin can adjust
    setApprovalAdjustmentReason("");
    setApprovalPaymentReceived(!!preReg.paymentReceived);
    setApprovingReg(preReg);
  };

  const confirmApprove = async () => {
    if (!approvingReg) return;
    const preReg = approvingReg;
    try {
      setProcessing(preReg.id);
      setApprovingReg(null);

      const updateResult = await client.models.EventPreRegistration.update({
        id: preReg.id,
        status: "APPROVED",
        amountPaid: approvalAmount,
        paymentReceived: approvalPaymentReceived,
        paymentAdjustmentReason: approvalAdjustmentReason || null,
      });

      if (updateResult.errors && updateResult.errors.length > 0) {
        showToast(`Failed to approve: ${updateResult.errors[0].message}`, "error");
        return;
      }

      // Send approval email
      const member = members[preReg.memberSub];
      const memberName = member ? `${member.firstName} ${member.lastName}` : "Member";
      const recipientEmail = member?.email || "";

      if (recipientEmail) {
        try {
          await (client.mutations as any).sendRegistrationEmail({
            recipientEmail,
            emailType: "REGISTRATION_APPROVED",
            memberName,
            eventId,
            eventTitle,
            eventDate,
          });
        } catch (emailErr) {
          console.error("Email sending failed:", emailErr);
          showToast("Registration approved, but email notification failed", "success");
        }
      }

      showToast("Registration approved successfully", "success");
      await loadPreRegistrations();
    } catch (err) {
      console.error("Error approving registration:", err);
      showToast("Failed to approve registration", "error");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (preReg: PreRegistration) => {
    const reason = window.prompt("Please provide a reason for rejection:");
    if (!reason) return; // User cancelled

    try {
      setProcessing(preReg.id);

      // Update status to REJECTED with admin notes
      const updateResult = await client.models.EventPreRegistration.update({
        id: preReg.id,
        status: "REJECTED",
        adminNotes: reason,
      });

      if (updateResult.errors && updateResult.errors.length > 0) {
        showToast(
          `Failed to reject: ${updateResult.errors[0].message}`,
          "error",
        );
        return;
      }

      // Send rejection email
      const member = members[preReg.memberSub];
      const memberName = member
        ? `${member.firstName} ${member.lastName}`
        : "Member";
      const recipientEmail = member?.email || "";

      if (recipientEmail) {
        try {
          await client.mutations.sendRegistrationEmail({
            recipientEmail,
            emailType: "REGISTRATION_REJECTED",
            memberName,
            eventId,
            eventTitle,
            eventDate,
            adminNotes: reason,
          });
        } catch (emailErr) {
          console.error("Email sending failed:", emailErr);
          // Don't block the rejection - just log the error
          showToast(
            "Registration rejected, but email notification failed",
            "success",
          );
        }
      }

      showToast("Registration rejected successfully", "success");
      await loadPreRegistrations();
    } catch (err) {
      console.error("Error rejecting registration:", err);
      showToast("Failed to reject registration", "error");
    } finally {
      setProcessing(null);
    }
  };

  const formatDateTime = (dateString: string) => {
    return formatClubDateTime(dateString, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      PENDING: "bg-yellow-100 text-yellow-800",
      APPROVED: "bg-green-100 text-green-800",
      REJECTED: "bg-red-100 text-red-800",
    };
    return styles[status as keyof typeof styles] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      {toast && (
        <div
          role="alert" aria-live="assertive"
          className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${
            toast.type === "success" ? "bg-green-600" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Competition Registration Management
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {eventTitle} - {formatDateTime(eventDate)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* #157 (S1): malformed-row tolerance notice. */}
          {unreadableCount > 0 && (
            <div role="status" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              {unreadableCount === 1 ? '1 unreadable record' : `${unreadableCount} unreadable records`} were skipped.
            </div>
          )}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadPreRegistrations}
                className="text-fishing-blue hover:text-blue-700"
              >
                Try Again
              </button>
            </div>
          ) : preRegistrations.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                No pre-registrations found
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registration Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount Paid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {preRegistrations.map((preReg) => {
                    const member = members[preReg.memberSub];
                    const memberName = member
                      ? `${member.firstName} ${member.lastName}`
                      : "Unknown Member";
                    const memberEmail = member?.email || preReg.memberSub;

                    return (
                      <tr key={preReg.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {memberName}
                          </div>
                          {!member && (
                            <div className="text-xs text-gray-500">
                              {preReg.memberSub}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {memberEmail}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDateTime(preReg.registrationDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {preReg.amountPaid != null ? `$${preReg.amountPaid}` : <span className="text-gray-400">—</span>}
                            {preReg.paymentAdjustmentReason && (
                              <div className="text-xs text-gray-500">{preReg.paymentAdjustmentReason}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {preReg.paymentReceived ? (
                            preReg.squarePaymentId ? (
                              <span className="inline-flex flex-col">
                                <span className="inline-flex items-center gap-1 text-sm font-semibold text-green-700">
                                  ✓ Paid via Square
                                </span>
                                <span className="text-xs text-gray-500 font-mono" title={preReg.squarePaymentId}>
                                  {preReg.squarePaymentId.slice(0, 12)}…
                                </span>
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center text-sm text-amber-700"
                                title="Marked as received manually by an admin — not verified by Square"
                              >
                                ✓ Marked paid (manual)
                              </span>
                            )
                          ) : (
                            <span className="text-sm text-yellow-600">Pending</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(
                              preReg.status || "PENDING",
                            )}`}
                          >
                            {preReg.status || "PENDING"}
                          </span>
                          {preReg.adminNotes && (
                            <div className="text-xs text-gray-500 mt-1">
                              Note: {preReg.adminNotes}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {preReg.status === "PENDING" ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprove(preReg)}
                                disabled={processing === preReg.id}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {processing === preReg.id ? (
                                  <>
                                    <svg
                                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    Processing...
                                  </>
                                ) : (
                                  "Approve"
                                )}
                              </button>
                              <button
                                onClick={() => handleReject(preReg)}
                                disabled={processing === preReg.id}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400">
                              {preReg.status === "APPROVED"
                                ? "Approved"
                                : "Rejected"}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>

      {/* Approval modal with payment fields */}
      {approvingReg && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setApprovingReg(null)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Approve Registration</h3>
              <p className="text-sm text-gray-600 mb-4">
                {members[approvingReg.memberSub]
                  ? `${members[approvingReg.memberSub].firstName} ${members[approvingReg.memberSub].lastName}`
                  : 'Member'}
              </p>
              <div className="space-y-4">
                {approvingReg.paymentReceived && approvingReg.squarePaymentId && (
                  <div className="bg-green-50 border border-green-300 rounded-lg p-3">
                    <p className="text-sm font-semibold text-green-800">
                      ✓ Payment verified by Square (${approvingReg.amountPaid?.toFixed(2) ?? approvalAmount.toFixed(2)})
                    </p>
                    <p className="text-xs text-green-700 mt-1 font-mono">Square ID: {approvingReg.squarePaymentId}</p>
                  </div>
                )}
                {approvingReg.paymentReceived && !approvingReg.squarePaymentId && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                    <p className="text-sm font-semibold text-amber-800">
                      ⚠️ Marked as paid manually — not verified by Square. Confirm payment was actually received (e.g. cash, bank transfer) before approving.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={approvalAmount}
                    onChange={(e) => setApprovalAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Reason (optional)</label>
                  <select
                    value={approvalAdjustmentReason.startsWith('Other') ? 'Other' : approvalAdjustmentReason}
                    onChange={(e) => setApprovalAdjustmentReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                  >
                    <option value="">No adjustment</option>
                    <option value="Committee member">Committee member</option>
                    <option value="Volunteer discount">Volunteer discount</option>
                    <option value="Family discount">Family discount</option>
                    <option value="Promotional offer">Promotional offer</option>
                    <option value="Financial hardship">Financial hardship</option>
                    <option value="Other">Other</option>
                  </select>
                  {approvalAdjustmentReason.startsWith('Other') && (
                    <input
                      type="text"
                      placeholder="Enter reason..."
                      value={approvalAdjustmentReason.startsWith('Other: ') ? approvalAdjustmentReason.slice(7) : ''}
                      onChange={(e) => setApprovalAdjustmentReason(e.target.value.trim() ? `Other: ${e.target.value}` : 'Other')}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="paymentReceived"
                    checked={approvalPaymentReceived}
                    disabled={!!approvingReg.squarePaymentId}
                    onChange={(e) => setApprovalPaymentReceived(e.target.checked)}
                    className="h-4 w-4 text-fishing-blue border-gray-300 rounded disabled:opacity-60"
                  />
                  <label htmlFor="paymentReceived" className="text-sm font-medium text-gray-700">
                    Payment received
                    {approvingReg.squarePaymentId && (
                      <span className="text-xs text-gray-500 font-normal"> (locked — verified by Square)</span>
                    )}
                  </label>
                </div>
              </div>
              {!approvalPaymentReceived && (
                <p className="text-xs text-amber-700 mt-3 text-right">
                  ⚠️ Approving without payment marked as received.
                </p>
              )}
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => setApprovingReg(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApprove}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
                >
                  Confirm Approval
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreRegistrationApproval;
